import { reviewService } from "../../services/review.service";
import { toast } from "react-toastify";
import {
  setReviews,
  setTempReviews,
  setReviewPagination,
  setReviewStats,
  setCustomerReview,
  setPendingReviews,
  setPendingPagination,
  setAllReviews,
  setAllReviewsPagination,
  setReviewSettings,
  setReviewIsLoading,
  setStatsLoading,
  setReviewError,
  openImagePopup,
  closeImagePopup,
  setImageIndex,
  nextImage,
  prevImage,
  addReview,
  updateReview,
  deleteReview,
  toggleReviewLike,
  approveReview,
  rejectReview,
  bulkApproveReviews,
  bulkRejectReviews,
} from "../reducers/reviewReducer";

// ==================== Basic Review Operations ====================

export const getProductReviewsData = (businessUuid, productId, params = {}) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.getProductReviews(businessUuid, productId, params);
      
      if (result?.success !== false) {
        // Handle different response structures
        let reviewsList = [];
        let pagination = { total: 0, page: 1, pages: 1, limit: 5 };
        
        if (result.reviews) {
          reviewsList = result.reviews;
          pagination = result.pagination || pagination;
        } else if (result.data?.reviews) {
          reviewsList = result.data.reviews;
          pagination = result.data.pagination || pagination;
        } else if (Array.isArray(result)) {
          reviewsList = result;
        } else if (result.data && Array.isArray(result.data)) {
          reviewsList = result.data;
        }
        
        dispatch(setReviews(reviewsList));
        dispatch(setTempReviews(reviewsList));
        dispatch(setReviewPagination(pagination));
      } else {
        dispatch(setReviewError(result?.message || "Failed to fetch reviews"));
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      dispatch(setReviewError(error.message || "An error occurred"));
    }
  };
};

export const getProductReviewStatsData = (businessUuid, productId) => {
  return async (dispatch) => {
    dispatch(setStatsLoading(true));
    try {
      const result = await reviewService.getProductReviewStats(businessUuid, productId);
      
      let statsData = {
        averageRating: 0,
        totalReviews: 0,
        verifiedReviews: 0,
        withPhotos: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      };
      
      if (result?.success !== false) {
        if (result.data) {
          statsData = {
            averageRating: result.data.averageRating || statsData.averageRating,
            totalReviews: result.data.totalReviews || statsData.totalReviews,
            verifiedReviews: result.data.verifiedReviews || statsData.verifiedReviews,
            withPhotos: result.data.withPhotos || statsData.withPhotos,
            ratingDistribution: result.data.ratingDistribution || statsData.ratingDistribution,
          };
        } else if (result.averageRating !== undefined) {
          statsData = {
            averageRating: result.averageRating || 0,
            totalReviews: result.totalReviews || 0,
            verifiedReviews: result.verifiedReviews || 0,
            withPhotos: result.withPhotos || 0,
            ratingDistribution: result.ratingDistribution || statsData.ratingDistribution,
          };
        }
      }
      
      dispatch(setReviewStats(statsData));
    } catch (error) {
      console.error("Error fetching review stats:", error);
      dispatch(setReviewError(error.message || "Failed to fetch review stats"));
    } finally {
      dispatch(setStatsLoading(false));
    }
  };
};

export const getCustomerProductReviewData = (businessUuid, productId, customerId) => {
  return async (dispatch) => {
    if (!customerId) return;
    
    try {
      const result = await reviewService.getCustomerProductReview(businessUuid, productId, customerId);
      
      if (result?.success && result.data) {
        dispatch(setCustomerReview(result.data));
      } else {
        dispatch(setCustomerReview(null));
      }
    } catch (error) {
      console.error("Error fetching customer review:", error);
      dispatch(setCustomerReview(null));
    }
  };
};

export const addReviewData = (businessUuid, productId, reviewData) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.addReview(businessUuid, productId, reviewData);
      
      if (result?.success) {
        
        // Refresh reviews and stats
        dispatch(getProductReviewsData(businessUuid, productId, { page: 1, limit: 5 }));
        dispatch(getProductReviewStatsData(businessUuid, productId));
        
        if (reviewData.customerId) {
          dispatch(getCustomerProductReviewData(businessUuid, productId, reviewData.customerId));
        }
        
        return result.data;
      } else {
        toast.error(result?.error || "Failed to add review");
        dispatch(setReviewError(result?.error || "Failed to add review"));
      }
    } catch (error) {
      console.error("Error adding review:", error);
      toast.error(error.message || "Failed to add review");
      dispatch(setReviewError(error.message || "Failed to add review"));
    }
  };
};

export const addReviewWithImagesData = (businessUuid, productId, formData) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.addReviewWithImages(businessUuid, productId, formData);
      
      if (result?.success) {
        
        // Refresh reviews and stats
        dispatch(getProductReviewsData(businessUuid, productId, { page: 1, limit: 5 }));
        dispatch(getProductReviewStatsData(businessUuid, productId));
        
        // Extract customerId from formData if possible
        const customerId = formData.get('customerId');
        if (customerId) {
          dispatch(getCustomerProductReviewData(businessUuid, productId, customerId));
        }
        
        return result.data;
      } else {
        toast.error(result?.error || "Failed to add review with images");
        dispatch(setReviewError(result?.error || "Failed to add review with images"));
      }
    } catch (error) {
      console.error("Error adding review with images:", error);
      toast.error(error.message || "Failed to add review with images");
      dispatch(setReviewError(error.message || "Failed to add review with images"));
    }
  };
};

// In reviewActions.js
export const likeReviewData = (businessUuid, reviewId, customerId) => {
  return async (dispatch) => {
    try {
      
      const result = await reviewService.likeReview(businessUuid, reviewId, customerId);
      

      if (result?.success) {
        // Extract the data
        const responseData = result.data;
        
        
        if (responseData && typeof responseData.isLiked !== 'undefined') {
          dispatch(toggleReviewLike({
            reviewId,
            isLiked: responseData.isLiked,
            likesCount: responseData.likesCount,
          }));
          
          toast.success(responseData.isLiked ? "Review liked!" : "Review unliked!");
          return { success: true, data: responseData };
        } else {
          console.error('Unexpected response structure:', responseData);
          toast.success("Review updated!"); // Still show success even if structure is unexpected
          return { success: true, data: responseData };
        }
      } else {
        const errorMsg = result?.error || "Failed to like review";
        toast.error(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("🔴 Action - Error in likeReviewData:", error);
      const errorMsg = error.message || "Failed to like review";
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };
};
export const deleteReviewData = (businessUuid, reviewId, customerId, productId) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.deleteReview(businessUuid, reviewId, customerId);
      
      if (result?.success) {
        toast.success("Review deleted successfully");
        
        // Refresh reviews and stats if productId is provided
        if (productId) {
          dispatch(getProductReviewsData(businessUuid, productId, { page: 1, limit: 5 }));
          dispatch(getProductReviewStatsData(businessUuid, productId));
          dispatch(setCustomerReview(null));
        }
      } else {
        toast.error(result?.error || "Failed to delete review");
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error(error.message || "Failed to delete review");
    }
  };
};

// ==================== Photo Operations ====================

export const uploadReviewPhotoData = (businessUuid, reviewId, photoFile) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.uploadReviewPhoto(businessUuid, reviewId, photoFile);
      
      if (result?.success) {
        toast.success("Photo uploaded successfully");
        return result.data;
      } else {
        toast.error(result?.error || "Failed to upload photo");
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error(error.message || "Failed to upload photo");
    }
  };
};

export const uploadReviewPhotosData = (businessUuid, reviewId, files) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.uploadReviewPhotos(businessUuid, reviewId, files);
      
      if (result?.success) {
        toast.success("Photos uploaded successfully");
        return result.data;
      } else {
        toast.error(result?.error || "Failed to upload photos");
      }
    } catch (error) {
      console.error("Error uploading photos:", error);
      toast.error(error.message || "Failed to upload photos");
    }
  };
};

export const removeReviewPhotoData = (businessUuid, reviewId, photoIndex, productId) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.removeReviewPhoto(businessUuid, reviewId, photoIndex);
      
      if (result?.success) {
        toast.success("Photo removed successfully");
        
        // Refresh reviews if productId is provided
        if (productId) {
          dispatch(getProductReviewsData(businessUuid, productId, { page: 1, limit: 5 }));
        }
      } else {
        toast.error(result?.error || "Failed to remove photo");
      }
    } catch (error) {
      console.error("Error removing photo:", error);
      toast.error(error.message || "Failed to remove photo");
    }
  };
};

// ==================== Admin Operations ====================

export const getPendingReviewsData = (businessUuid, page = 1, limit = 20) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.getPendingReviews(businessUuid, page, limit);
      
      if (result?.success) {
        dispatch(setPendingReviews(result.reviews || []));
        dispatch(setPendingPagination(result.pagination || { total: 0, page, pages: 1, limit }));
      } else {
        dispatch(setPendingReviews([]));
      }
    } catch (error) {
      console.error("Error fetching pending reviews:", error);
      dispatch(setReviewError(error.message || "Failed to fetch pending reviews"));
    }
  };
};

export const getAllReviewsData = (businessUuid, params = {}) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.getAllReviews(businessUuid, params);
      
      if (result?.success) {
        dispatch(setAllReviews(result.reviews || []));
        dispatch(setAllReviewsPagination(result.pagination || { total: 0, page: 1, pages: 1, limit: 20 }));
      } else {
        dispatch(setAllReviews([]));
      }
    } catch (error) {
      console.error("Error fetching all reviews:", error);
      dispatch(setReviewError(error.message || "Failed to fetch all reviews"));
    }
  };
};

export const approveReviewData = (businessUuid, reviewId, adminId, verificationNotes = '') => {
  return async (dispatch) => {
    try {
      const result = await reviewService.approveReview(businessUuid, reviewId, adminId, verificationNotes);
      
      if (result?.success) {
        toast.success("Review approved successfully");
        dispatch(approveReview({ reviewId, review: result.data }));
        
        // Refresh pending reviews
        dispatch(getPendingReviewsData(businessUuid));
      } else {
        toast.error(result?.error || "Failed to approve review");
      }
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error(error.message || "Failed to approve review");
    }
  };
};

export const rejectReviewData = (businessUuid, reviewId, adminId, rejectionReason) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.rejectReview(businessUuid, reviewId, adminId, rejectionReason);
      
      if (result?.success) {
        toast.success("Review rejected successfully");
        dispatch(rejectReview({ reviewId }));
        
        // Refresh pending reviews
        dispatch(getPendingReviewsData(businessUuid));
      } else {
        toast.error(result?.error || "Failed to reject review");
      }
    } catch (error) {
      console.error("Error rejecting review:", error);
      toast.error(error.message || "Failed to reject review");
    }
  };
};

export const removeReviewData = (businessUuid, reviewId, adminId, removalReason, productId) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.removeReview(businessUuid, reviewId, adminId, removalReason);
      
      if (result?.success) {
        toast.success("Review removed successfully");
        
        // Refresh if needed
        if (productId) {
          dispatch(getProductReviewStatsData(businessUuid, productId));
        }
      } else {
        toast.error(result?.error || "Failed to remove review");
      }
    } catch (error) {
      console.error("Error removing review:", error);
      toast.error(error.message || "Failed to remove review");
    }
  };
};

export const restoreReviewData = (businessUuid, reviewId, adminId, restorationNotes) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.restoreReview(businessUuid, reviewId, adminId, restorationNotes);
      
      if (result?.success) {
        toast.success("Review restored successfully");
      } else {
        toast.error(result?.error || "Failed to restore review");
      }
    } catch (error) {
      console.error("Error restoring review:", error);
      toast.error(error.message || "Failed to restore review");
    }
  };
};

// ==================== Bulk Admin Operations ====================

export const bulkApproveReviewsData = (businessUuid, reviewIds, adminId) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.bulkApproveReviews(businessUuid, reviewIds, adminId);
      
      if (result?.success) {
        toast.success(`${reviewIds.length} reviews approved successfully`);
        dispatch(bulkApproveReviews({ reviewIds }));
        
        // Refresh pending reviews
        dispatch(getPendingReviewsData(businessUuid));
      } else {
        toast.error(result?.error || "Failed to bulk approve reviews");
      }
    } catch (error) {
      console.error("Error bulk approving reviews:", error);
      toast.error(error.message || "Failed to bulk approve reviews");
    }
  };
};

export const bulkRejectReviewsData = (businessUuid, reviewIds, adminId, rejectionReason) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.bulkRejectReviews(businessUuid, reviewIds, adminId, rejectionReason);
      
      if (result?.success) {
        toast.success(`${reviewIds.length} reviews rejected successfully`);
        dispatch(bulkRejectReviews({ reviewIds }));
        
        // Refresh pending reviews
        dispatch(getPendingReviewsData(businessUuid));
      } else {
        toast.error(result?.error || "Failed to bulk reject reviews");
      }
    } catch (error) {
      console.error("Error bulk rejecting reviews:", error);
      toast.error(error.message || "Failed to bulk reject reviews");
    }
  };
};

export const bulkRemoveReviewsData = (businessUuid, reviewIds, adminId, removalReason) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.bulkRemoveReviews(businessUuid, reviewIds, adminId, removalReason);
      
      if (result?.success) {
        toast.success(`${reviewIds.length} reviews removed successfully`);
      } else {
        toast.error(result?.error || "Failed to bulk remove reviews");
      }
    } catch (error) {
      console.error("Error bulk removing reviews:", error);
      toast.error(error.message || "Failed to bulk remove reviews");
    }
  };
};

export const bulkRestoreReviewsData = (businessUuid, reviewIds, adminId, restorationNotes) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.bulkRestoreReviews(businessUuid, reviewIds, adminId, restorationNotes);
      
      if (result?.success) {
        toast.success(`${reviewIds.length} reviews restored successfully`);
      } else {
        toast.error(result?.error || "Failed to bulk restore reviews");
      }
    } catch (error) {
      console.error("Error bulk restoring reviews:", error);
      toast.error(error.message || "Failed to bulk restore reviews");
    }
  };
};

// ==================== Settings Operations ====================

export const getStoreReviewSettingsData = (businessUuid) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.getStoreReviewSettings(businessUuid);
      
      if (result?.success) {
        dispatch(setReviewSettings(result.data));
      } else {
        dispatch(setReviewSettings(null));
      }
    } catch (error) {
      console.error("Error fetching review settings:", error);
      dispatch(setReviewSettings(null));
    }
  };
};

export const updateStoreReviewSettingsData = (businessUuid, settings) => {
  return async (dispatch) => {
    dispatch(setReviewIsLoading());
    try {
      const result = await reviewService.updateStoreReviewSettings(businessUuid, settings);
      
      if (result?.success) {
        toast.success("Review settings updated successfully");
        dispatch(setReviewSettings(result.data));
        return result.data;
      } else {
        toast.error(result?.error || "Failed to update review settings");
      }
    } catch (error) {
      console.error("Error updating review settings:", error);
      toast.error(error.message || "Failed to update review settings");
    }
  };
};

// ==================== Check Eligibility ====================

export const checkCustomerCanReviewData = (businessUuid, productId, customerId) => {
  return async () => {
    try {
      const result = await reviewService.checkCustomerCanReview(businessUuid, productId, customerId);
      return result;
    } catch (error) {
      console.error("Error checking review eligibility:", error);
      return {
        success: false,
        canReview: false,
        message: "Unable to check review eligibility"
      };
    }
  };
};

// ==================== Image Popup Actions ====================

export const openReviewImagePopup = (images, startIndex = 0) => (dispatch) => {
  dispatch(openImagePopup({ images, startIndex }));
  document.body.style.overflow = "hidden";
};

export const closeReviewImagePopup = () => (dispatch) => {
  dispatch(closeImagePopup());
  document.body.style.overflow = "unset";
};

export const setReviewImageIndex = (index) => (dispatch) => {
  dispatch(setImageIndex(index));
};

export const nextReviewImage = () => (dispatch) => {
  dispatch(nextImage());
};

export const prevReviewImage = () => (dispatch) => {
  dispatch(prevImage());
};

// ==================== Utility ====================

export const getReviewStatsData = (businessUuid) => {
  return async (dispatch) => {
    try {
      const result = await reviewService.getReviewStats(businessUuid);
      
      if (result?.success) {
        return result.data;
      }
      return { total: 0, approved: 0, pending: 0 };
    } catch (error) {
      console.error("Error fetching review stats:", error);
      return { total: 0, approved: 0, pending: 0 };
    }
  };
};